import { combineReducers } from 'redux';
import * as types from 'legacy/src/constants/types';
import { authorize, login } from 'legacy/src/components/auth/actions';
import {
  isAfterEducationPeriodExpires,
  isCurrentEducationPeriod,
} from '../utility/EducationPeriodCalculations';
import { isImpersonating } from 'legacy/src/components/impersonating/utils';

function checkIfAuthenticated() {
  const token = window.localStorage.getItem('account_access_token');
  return !(token === null || token === undefined);
}

/**
 * Overrides the ssoLogin for impersonated student to bypass Levity
 * @param {object} me The me query response from redux
 * @returns {object} me with new ssoLogin
 *
 * */
const setImpersonatingMeOverrides = (me) => {
  if (isImpersonating()) {
    return {
      ...me,
      ssoLogin: 'Not SSO',
    };
  }

  return me;
};

const isAuthenticated = (state = false, action) => {
  switch (action.type) {
    case login.SUCCESS:
      return checkIfAuthenticated();
    case authorize.SUCCESS:
      return checkIfAuthenticated();
    default:
      return state;
  }
};

const isAdmin = (state = false, action) => {
  switch (action.type) {
    case login.SUCCESS:
      return action.payload.me.userRole.includes('admin');
    case authorize.SUCCESS:
      return action.payload.me.userRole.includes('admin');
    default:
      return state;
  }
};

const isCoordinator = (state = false, action) => {
  switch (action.type) {
    case login.SUCCESS:
      return action.payload.me.userRole.includes('coordinator');
    case authorize.SUCCESS:
      return action.payload.me.userRole.includes('coordinator');
    default:
      return state;
  }
};

const isStaff = (state = false, action) => {
  switch (action.type) {
    case login.SUCCESS:
      return action.payload.me.userRole.includes('staff');
    case authorize.SUCCESS:
      return action.payload.me.userRole.includes('staff');
    default:
      return state;
  }
};

const isDemo = (state = false, action) => {
  switch (action.type) {
    case login.SUCCESS:
      return action.payload.me.userRole.includes('demo');
    case authorize.SUCCESS:
      return action.payload.me.userRole.includes('demo');
    default:
      return state;
  }
};

const isStudent = (state = false, action) => {
  switch (action.type) {
    case types.LOGOUT:
      return false;
    case login.FAILURE:
      return false;
    case login.SUCCESS:
      return action.payload.me.userRole.includes('student');
    case authorize.SUCCESS:
      return action.payload.me.userRole.includes('student');
    default:
      return state;
  }
};

const isTeacher = (state = false, action) => {
  switch (action.type) {
    case types.LOGOUT:
      return false;
    case login.FAILURE:
      return false;
    case login.SUCCESS:
      return action.payload.me.userRole.includes('teacher');
    case authorize.SUCCESS:
      return action.payload.me.userRole.includes('teacher');
    default:
      return state;
  }
};

const me = (state = false, action) => {
  switch (action.type) {
    case login.SUCCESS:
      if (action.payload) {
        return setImpersonatingMeOverrides(action.payload.me);
      }
      return state;
    case types.SIGNUP_SUCCESS:
      if (action.payload) {
        return setImpersonatingMeOverrides(action.payload.me);
      }
      return state;
    case types.LOGOUT_ERROR:
    case login.FAILURE:
      return false;
    case types.SIGNUP_ERROR:
      return false;
    case types.LOGOUT_SUCCESS:
      return false;
    case authorize.SUCCESS:
      if (action.payload) {
        return setImpersonatingMeOverrides(action.payload.me);
      }
      return state;
    case types.SAVE_PREFERENCE_SUCCESS:
      if (action.payload) return action.payload;
      return state;
    default:
      return state;
  }
};

const teacherSubjects = (state = [], action) => {
  switch (action.type) {
    case login.SUCCESS:
      if (action.payload) return action.payload.teacherSubjects || [];
      return state;
    case types.SIGNUP_SUCCESS:
      if (action.payload) return action.payload.teacherSubjects || [];
      return state;
    case types.LOGOUT_ERROR:
    case login.FAILURE:
    case types.SIGNUP_ERROR:
    case types.LOGOUT_SUCCESS:
      return [];
    case authorize.SUCCESS:
      if (action.payload) return action.payload.teacherSubjects || [];
      return state;
    default:
      return state;
  }
};

const studentSubjects = (state = [], action) => {
  switch (action.type) {
    case login.SUCCESS:
      if (action.payload) return action.payload.studentSubjects || [];
      return state;
    case types.SIGNUP_SUCCESS:
      if (action.payload) return action.payload.studentSubjects || [];
      return state;
    case types.LOGOUT_ERROR:
    case login.FAILURE:
    case types.SIGNUP_ERROR:
    case types.LOGOUT_SUCCESS:
      return [];
    case authorize.SUCCESS:
      if (action.payload) return action.payload.studentSubjects || [];
      return state;
    default:
      return state;
  }
};

const adminSubjects = (state = [], action) => {
  switch (action.type) {
    case login.SUCCESS:
      if (action.payload) return action.payload.adminSubjects || [];
      return state;
    case types.SIGNUP_SUCCESS:
      if (action.payload) return action.payload.adminSubjects || [];
      return state;
    case types.LOGOUT_ERROR:
    case login.FAILURE:
    case types.SIGNUP_ERROR:
    case types.LOGOUT_SUCCESS:
      return [];
    case authorize.SUCCESS:
      if (action.payload) return action.payload.adminSubjects || [];
      return state;
    default:
      return state;
  }
};

const educationPeriod = (state = {}, action) => {
  switch (action.type) {
    case login.SUCCESS:
      if (action.payload) return action.payload.educationPeriod || {};
      return state;
    case types.SIGNUP_SUCCESS:
      if (action.payload) return action.payload.educationPeriod || {};
      return state;
    case types.LOGOUT_ERROR:
    case login.FAILURE:
    case types.SIGNUP_ERROR:
    case types.LOGOUT_SUCCESS:
      return {};
    case authorize.SUCCESS:
      if (action.payload) return action.payload.educationPeriod || {};
      return state;
    default:
      return state;
  }
};

const chosenEducationPeriodCode = (state = '', action) => {
  switch (action.type) {
    case types.SET_DEFAULT_EDUCATION_PERIOD:
      if (action.payload) return action.payload.educationPeriodCode || '';
      return state;
    case types.LOGOUT_ERROR:
    case login.FAILURE:
    case types.SIGNUP_ERROR:
    case types.LOGOUT_SUCCESS:
      return '';
    default:
      return state;
  }
};

const isContentExpiring = (state = false, action) => {
  const isExpiring = (edCode, nextEdCode, periods) =>
    isCurrentEducationPeriod(edCode, periods) &&
    isCurrentEducationPeriod(nextEdCode, periods);
  switch (action.type) {
    case types.SET_DEFAULT_EDUCATION_PERIOD:
      if (action.payload) {
        const edCode = action.payload.educationPeriodCode;
        const nextCode = (parseInt(edCode, 10) + 1).toString();
        const { allEducationPeriods } = action.payload;
        return isExpiring(edCode, nextCode, allEducationPeriods);
      }
      return state;
    case types.LOGOUT_ERROR:
    case login.FAILURE:
    case types.SIGNUP_ERROR:
    case types.LOGOUT_SUCCESS:
      return false;
    default:
      return state;
  }
};

const isNewContentAvailable = (state = false, action) => {
  const isAvailable = (edCode, previousCode, periods) =>
    isCurrentEducationPeriod(edCode, periods) &&
    isCurrentEducationPeriod(previousCode, periods);

  switch (action.type) {
    case types.SET_DEFAULT_EDUCATION_PERIOD:
      if (action.payload) {
        const edCode = action.payload.educationPeriodCode;
        const previousCode = (parseInt(edCode, 10) - 1).toString();
        const { allEducationPeriods } = action.payload;
        return isAvailable(edCode, previousCode, allEducationPeriods);
      }
      return state;
    case types.LOGOUT_ERROR:
    case login.FAILURE:
    case types.SIGNUP_ERROR:
    case types.LOGOUT_SUCCESS:
      return false;
    default:
      return state;
  }
};

const hasAccessToNextYear = (state = false, action) => {
  switch (action.type) {
    case types.SET_DEFAULT_EDUCATION_PERIOD:
      if (action.payload) {
        const {
          educationPeriod: { nextEducationPeriod },
          subjectEducationPeriods,
        } = action.payload;
        const nextEducationPeriodCode =
          subjectEducationPeriods &&
          subjectEducationPeriods.find(
            (period) => period.id === nextEducationPeriod.id,
          );

        return !!nextEducationPeriodCode;
      }
      return state;
    case types.LOGOUT_ERROR:
    case login.FAILURE:
    case types.SIGNUP_ERROR:
    case types.LOGOUT_SUCCESS:
      return false;
    default:
      return state;
  }
};

const isNewTeacher = (state = false, action) => {
  switch (action.type) {
    case types.SET_DEFAULT_EDUCATION_PERIOD:
      if (action.payload) {
        const {
          educationPeriod: { currentEducationPeriod },
          subjectEducationPeriods,
        } = action.payload;
        const currentEducationPeriodCode =
          subjectEducationPeriods &&
          subjectEducationPeriods.find(
            (period) => period.id === currentEducationPeriod.id,
          );

        return !currentEducationPeriodCode;
      }
      return state;
    case types.LOGOUT_ERROR:
    case login.FAILURE:
    case types.SIGNUP_ERROR:
    case types.LOGOUT_SUCCESS:
      return false;
    default:
      return state;
  }
};

const isContentExpired = (state = false, action) => {
  switch (action.type) {
    case types.SET_DEFAULT_EDUCATION_PERIOD:
      if (action.payload)
        return isAfterEducationPeriodExpires(
          action.payload.educationPeriodCode,
          action.payload.allEducationPeriods,
        );
      return state;
    case types.LOGOUT_ERROR:
    case login.FAILURE:
    case types.SIGNUP_ERROR:
    case types.LOGOUT_SUCCESS:
      return false;
    default:
      return state;
  }
};

const userEducationPeriods = (state = [], action) => {
  switch (action.type) {
    case types.SET_USER_EDUCATION_PERIODS:
      return action.payload;
    default:
      return state;
  }
};

const meReducer = combineReducers({
  isTeacher,
  isStudent,
  isAdmin,
  isCoordinator,
  isStaff,
  isDemo,
  isAuthenticated,
  isImpersonating,
  me,
  teacherSubjects,
  studentSubjects,
  adminSubjects,
  educationPeriod,
  chosenEducationPeriodCode,
  userEducationPeriods,
  isContentExpiring,
  isNewContentAvailable,
  hasAccessToNextYear,
  isNewTeacher,
  isContentExpired,
});

export default meReducer;
